// PreUploadForm.jsx — /upload?t=<token> (client-facing pre-event upload portal)
//
// Public bearer-token page. Clients (event organizers, classroom teachers,
// etc.) get a link in a Brevo email and use it to stage custom designs
// against a specific booking ahead of the event. Uploads land in state
// "staged" until staff promotes them at the booth.
//
// Architecture:
// - Token validated on load (callable-less; reads tokens/{id} directly since
//   firestore.rules allow public read on status active/exhausted/revoked).
// - Submit / edit / trash / digest all go through Cloud Function callables
//   so cap enforcement and bearer-auth happen server-side.
// - Photo uploads land in Storage at preuploads/{tokenId}/{uuid}.jpg before
//   the order doc is created; orphan files past 7 days are swept by a future
//   cleanup task (not yet wired — minor).
// - Visual language matches CommissionUpload (navy/blue/cream radial bg).
// - The page shows event title pulled from the linked booking when present.

import { useEffect, useMemo, useRef, useState } from "react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, storage } from "../firebase";
import FramingTool from "../FramingTool";
import { COLOR_OPTIONS } from "../flashDesigns";
import { normalizePhotoForUpload, PHOTO_ACCEPT_ATTR } from "../utils/photo";

const B = {
  navy:   "#1d52a0",
  blue:   "#1f59db",
  sky:    "#4aa4e1",
  orange: "#f57600",
  gold:   "#fcb31f",
  pink:   "#ef537e",
  cream:  "#ffdaad",
  dark:   "#0d1833",
  green:  "#70be44",
};

const BG = `radial-gradient(ellipse 140% 95% at 50% 0%,
  #1f59db 0%, #1a4b9e 30%, #0e2e46 70%, #081a2a 100%)`;

const STEPS = ["Photo", "Position", "Details", "Review"];

const fns = () => getFunctions(getApp());

export default function PreUploadForm() {
  const tokenId = useMemo(() => new URLSearchParams(window.location.search).get("t"), []);

  const [loading, setLoading]   = useState(true);
  const [token, setToken]       = useState(null);
  const [booking, setBooking]   = useState(null);
  const [orders, setOrders]     = useState([]);
  const [loadError, setLoadError] = useState(null);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);

  const [digestEmail, setDigestEmail] = useState("");
  const [digestStatus, setDigestStatus] = useState(null); // "sending" | "sent" | "error"

  // Subscribe to the token doc directly. Tokens are public-readable when
  // status ∈ {active, exhausted, revoked} per firestore.rules — no auth
  // needed. Bearer-token in the URL is the credential. The token also
  // holds an orderIds[] index that drives the orders fetch below.
  useEffect(() => {
    if (!tokenId) { setLoadError("No upload link supplied."); setLoading(false); return; }
    const unsub = onSnapshot(
      doc(db, "tokens", tokenId),
      (snap) => {
        if (!snap.exists()) { setLoadError("This upload link wasn't found. Double-check the URL or get in touch."); setLoading(false); return; }
        const t = snap.data();
        if (t.source !== "preupload") { setLoadError("This isn't a pre-upload link."); setLoading(false); return; }
        setToken({ id: snap.id, ...t });
        setLoading(false);
      },
      (err) => {
        console.warn("token snapshot error:", err);
        setLoadError("Couldn't load the upload link.");
        setLoading(false);
      },
    );
    return () => unsub();
  }, [tokenId]);

  // One-shot booking fetch once we know the token's bookingId. Bookings
  // are staff-only-read, so this only resolves for signed-in staff. Public
  // visitors get the fallback "your event" title — fine.
  useEffect(() => {
    if (!token?.bookingId) return;
    getDoc(doc(db, "bookings", token.bookingId))
      .then((bSnap) => { if (bSnap.exists()) setBooking({ id: bSnap.id, ...bSnap.data() }); })
      .catch(() => { /* public visitors can't read bookings — that's fine */ });
  }, [token?.bookingId]);

  // Resolve orders by fetching each id from token.orderIds[]. getDoc on
  // an order is publicly allowed (`allow get: if true`); only the list
  // query needed auth. Refires whenever orderIds changes (submit / trash
  // mutate it server-side, and the token snapshot above wakes us up).
  useEffect(() => {
    const ids = Array.isArray(token?.orderIds) ? token.orderIds : null;
    if (!ids || ids.length === 0) { setOrders([]); return; }
    let cancelled = false;
    Promise.all(
      ids.map(id => getDoc(doc(db, "orders", id))
        .then(s => s.exists() ? { id: s.id, ...s.data() } : null)
        .catch(() => null))
    ).then(results => {
      if (cancelled) return;
      const list = results
        .filter(o => o && !o.trashedAt)
        .sort((a, b) => (a.stagedAt?.toMillis?.() ?? 0) - (b.stagedAt?.toMillis?.() ?? 0));
      setOrders(list);
    });
    return () => { cancelled = true; };
  // Compare by content not array reference identity.
  }, [token?.orderIds?.join(",")]);

  const eventTitle = booking?.publicTitle || booking?.eventName || "your event";
  const usedCount = orders.length;
  const cap = token?.maxUploads ?? 0;
  const canSubmitMore = token && token.status !== "revoked" && usedCount < cap;
  const tokenExpired = token?.expiresAt && token.expiresAt.toMillis?.() < Date.now();

  async function handleSendDigest() {
    if (!digestEmail.includes("@")) return;
    setDigestStatus("sending");
    try {
      const fn = httpsCallable(fns(), "emailPreUploadList");
      await fn({ tokenId, toEmail: digestEmail.trim() });
      setDigestStatus("sent");
    } catch (err) {
      setDigestStatus("error");
      console.warn("digest send failed", err);
    }
  }

  // ── render branches ──────────────────────────────────────────────────────

  if (loading) {
    return <CenteredScreen text="Loading your upload page…" />;
  }
  if (loadError || !token) {
    return <CenteredScreen text={loadError || "Couldn't load this upload page."} tone="error" />;
  }
  if (token.status === "revoked") {
    return <CenteredScreen text="This upload link has been revoked. Please get back in touch with us about your event." tone="error" />;
  }
  if (tokenExpired) {
    return <CenteredScreen text="This upload link has expired. Please get back in touch with us about your event." tone="error" />;
  }

  // ── main page ────────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: "100vh", background: BG, padding: "32px clamp(16px, 4vw, 48px) 80px" }}>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>

        {/* Header card */}
        <div style={{
          background: "rgba(255,255,255,0.06)", border: `1px solid rgba(252,179,31,0.45)`,
          borderRadius: 16, padding: "24px 22px", color: B.cream, marginBottom: 22,
        }}>
          <div style={{ color: B.gold, fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 800 }}>
            Pre-upload portal
          </div>
          <h1 style={{ margin: "8px 0 6px", fontFamily: "Fredoka, var(--jop-display)", fontSize: 30, color: "#fff", lineHeight: 1.15 }}>
            Designs for <span style={{ color: B.gold }}>{eventTitle}</span>
          </h1>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, opacity: 0.88 }}>
            Upload reference photos for each pancake you'd like us to draw. You can come back to this link to add, edit, or remove anytime before the event.
          </p>
          <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <span style={{ background: "rgba(252,179,31,0.16)", color: B.gold, fontWeight: 700, fontSize: 13, padding: "5px 12px", borderRadius: 999 }}>
              {usedCount} of {cap} uploaded
            </span>
            {!canSubmitMore && (
              <span style={{ background: "rgba(239,83,126,0.15)", color: B.pink, fontWeight: 700, fontSize: 13, padding: "5px 12px", borderRadius: 999 }}>
                Cap reached
              </span>
            )}
          </div>
        </div>

        {/* Existing uploads */}
        {orders.length > 0 && (
          <div style={{ marginBottom: 22 }}>
            <div style={{ color: B.cream, fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700, marginBottom: 10, opacity: 0.8 }}>
              Your uploads
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
              {orders.map(o => (
                <UploadCard
                  key={o.id}
                  order={o}
                  tokenId={tokenId}
                  onEdit={() => { setEditingOrder(o); setWizardOpen(true); }}
                  onTrashed={() => { /* live snapshot handles it */ }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Add a design button */}
        {canSubmitMore && (
          <button
            onClick={() => { setEditingOrder(null); setWizardOpen(true); }}
            style={{
              display: "block", width: "100%", padding: "16px 18px",
              background: B.gold, color: B.dark, border: "none", borderRadius: 14,
              fontFamily: "Fredoka, var(--jop-display)", fontWeight: 800, fontSize: 18,
              cursor: "pointer", marginBottom: 22,
            }}
          >
            + Add a design
          </button>
        )}

        {/* Digest email — explicit, user-triggered. */}
        <div style={{
          background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,218,173,0.2)",
          borderRadius: 12, padding: "16px 18px", color: B.cream,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>📧 Email me a copy of my uploads</div>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>
            We'll send the current list to whatever address you want — useful as a record for yourself or to forward.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <input
              type="email"
              placeholder="you@example.com"
              value={digestEmail}
              onChange={e => { setDigestEmail(e.target.value); setDigestStatus(null); }}
              style={{
                flex: 1, minWidth: 200, padding: "10px 12px",
                background: "rgba(0,0,0,0.25)", color: "#fff", fontSize: 14,
                border: "1px solid rgba(255,255,255,0.18)", borderRadius: 8,
              }}
            />
            <button
              onClick={handleSendDigest}
              disabled={!digestEmail.includes("@") || digestStatus === "sending"}
              style={{
                padding: "10px 16px", background: B.sky, color: "#fff", border: "none", borderRadius: 8,
                fontWeight: 700, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
                opacity: (!digestEmail.includes("@") || digestStatus === "sending") ? 0.55 : 1,
              }}
            >
              {digestStatus === "sending" ? "Sending…" : digestStatus === "sent" ? "✓ Sent" : "Send"}
            </button>
          </div>
          {digestStatus === "error" && (
            <div style={{ color: B.pink, fontSize: 12, marginTop: 8 }}>Couldn't send — try again in a moment.</div>
          )}
        </div>

      </div>

      {wizardOpen && (
        <WizardModal
          tokenId={tokenId}
          editingOrder={editingOrder}
          onClose={() => { setWizardOpen(false); setEditingOrder(null); }}
        />
      )}
    </div>
  );
}

// ── UploadCard ────────────────────────────────────────────────────────────

function UploadCard({ order, tokenId, onEdit }) {
  const [trashing, setTrashing] = useState(false);
  const thumb = order.croppedImageUrl || order.imageUrl;
  const isPromoted = order.state !== "staged";

  async function handleTrash() {
    if (!window.confirm(`Remove "${order.recipientName}" from your uploads?`)) return;
    setTrashing(true);
    try {
      const fn = httpsCallable(fns(), "trashPreUpload");
      await fn({ tokenId, orderId: order.id });
    } catch (err) {
      alert("Couldn't remove that upload: " + (err?.message || "try again"));
      setTrashing(false);
    }
  }

  return (
    <div style={{
      background: "rgba(255,255,255,0.06)",
      border: `1px solid ${isPromoted ? "rgba(112,190,68,0.5)" : "rgba(255,218,173,0.25)"}`,
      borderRadius: 12, overflow: "hidden",
    }}>
      <div style={{ aspectRatio: "1", background: "#000" }}>
        {thumb && <img src={thumb} alt={order.recipientName} style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />}
      </div>
      <div style={{ padding: "10px 12px", color: B.cream }}>
        <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {order.recipientName || "Unnamed"}
        </div>
        {order.notes && (
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {order.notes}
          </div>
        )}
        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
          {isPromoted ? (
            <span style={{ flex: 1, textAlign: "center", color: B.green, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              ✓ In the queue
            </span>
          ) : (
            <>
              <button
                onClick={onEdit}
                style={{ flex: 1, padding: "6px 8px", background: "transparent", color: B.sky, border: `1px solid ${B.sky}`, borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
              >
                Edit
              </button>
              <button
                onClick={handleTrash}
                disabled={trashing}
                style={{ flex: 1, padding: "6px 8px", background: "transparent", color: B.pink, border: `1px solid ${B.pink}`, borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: trashing ? 0.5 : 1 }}
              >
                {trashing ? "…" : "Remove"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── WizardModal ───────────────────────────────────────────────────────────

function WizardModal({ tokenId, editingOrder, onClose }) {
  const isEdit = !!editingOrder;
  const [step, setStep] = useState(isEdit ? 2 : 0);   // edit jumps to details
  const [photoFile, setPhotoFile]   = useState(null);
  const [photoPreview, setPhotoPreview] = useState(editingOrder?.imageUrl || null);
  const [croppedBlob, setCroppedBlob] = useState(null);
  // croppedUrl: starts as the existing Storage URL (edit mode) so the review
  // step has something to show before the user touches anything; switches to
  // a blob: URL the moment the user reframes or picks a new photo. The
  // useEffect below keeps it in sync with croppedBlob.
  const [croppedUrl, setCroppedUrl] = useState(editingOrder?.croppedImageUrl || null);
  useEffect(() => {
    if (!croppedBlob) return;
    const url = URL.createObjectURL(croppedBlob);
    setCroppedUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [croppedBlob]);
  const [framingState, setFramingState] = useState(null);
  const [name, setName]   = useState(editingOrder?.recipientName || "");
  const [notes, setNotes] = useState(editingOrder?.notes || "");
  const [colors, setColors] = useState(editingOrder?.favoriteColors || []);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const fileRef = useRef(null);

  async function handleFile(e) {
    const raw = e.target.files?.[0];
    // Clear the input value so picking the same file twice still re-fires
    // the change event (useful after a failed convert / rejected format).
    if (e.target) e.target.value = "";
    if (!raw) return;
    setPhotoError(null);
    setPhotoLoading(true);
    try {
      const { file, error } = await normalizePhotoForUpload(raw);
      if (error) {
        setPhotoError(error);
        setPhotoLoading(false);
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
      setCroppedBlob(null);
      setPhotoLoading(false);
      setStep(1);
    } catch (err) {
      console.warn("photo normalize threw:", err);
      setPhotoError("Couldn't process that file. Try a different photo (JPEG or PNG works best).");
      setPhotoLoading(false);
    }
  }

  function toggleColor(id) {
    setColors(prev => prev.includes(id) ? prev.filter(c => c !== id) : prev.length >= 3 ? prev : [...prev, id]);
  }

  async function uploadIfNeeded() {
    // Edit-mode shortcut: if the user didn't change the photo, reuse the URLs.
    if (isEdit && !photoFile && !croppedBlob) {
      return { imageUrl: editingOrder.imageUrl, croppedImageUrl: editingOrder.croppedImageUrl };
    }
    if (!croppedBlob || !photoFile) {
      throw new Error("Please add a photo and position it.");
    }
    const uid = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const origRef = storageRef(storage, `preuploads/${tokenId}/${uid}.jpg`);
    const cropRef = storageRef(storage, `preuploads/${tokenId}/${uid}_cropped.jpg`);
    await uploadBytes(origRef, photoFile, { contentType: photoFile.type || "image/jpeg" });
    await uploadBytes(cropRef, croppedBlob, { contentType: "image/jpeg" });
    const [imageUrl, croppedImageUrl] = await Promise.all([
      getDownloadURL(origRef),
      getDownloadURL(cropRef),
    ]);
    return { imageUrl, croppedImageUrl };
  }

  async function handleSubmit() {
    setSubmitError(null);
    if (!name.trim()) { setSubmitError("Guest name is required."); setStep(2); return; }
    setSubmitting(true);
    try {
      const { imageUrl, croppedImageUrl } = await uploadIfNeeded();
      const framing = framingState ? {
        framingScale: framingState.scale,
        framingOffsetXFrac: framingState.offsetXFrac,
        framingOffsetYFrac: framingState.offsetYFrac,
        framingDrawnWidthFrac: framingState.drawnWidthFrac,
        framingDrawnHeightFrac: framingState.drawnHeightFrac,
        framingRotation: framingState.rotation || 0,
      } : {};
      const payload = {
        recipientName: name.trim(),
        notes: notes.trim() || null,
        favoriteColors: colors,
        imageUrl,
        croppedImageUrl,
        ...framing,
      };
      if (isEdit) {
        const fn = httpsCallable(fns(), "updatePreUpload");
        await fn({ tokenId, orderId: editingOrder.id, changes: payload });
      } else {
        const fn = httpsCallable(fns(), "submitPreUpload");
        await fn({ tokenId, fields: payload });
      }
      onClose();
    } catch (err) {
      setSubmitError(err?.message || "Couldn't save. Try again.");
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(8,26,42,0.85)", zIndex: 50, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "24px 16px", overflowY: "auto" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: "100%", maxWidth: 520, background: B.dark, border: `1px solid rgba(252,179,31,0.4)`, borderRadius: 16, color: B.cream, padding: "22px 22px 24px" }}>

        {/* Step bar */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
          {STEPS.map((label, i) => (
            <div key={label} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
              <div style={{ width: 26, height: 26, borderRadius: 999, background: i <= step ? B.gold : "rgba(255,255,255,0.12)", color: i <= step ? B.dark : B.cream, fontWeight: 800, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {i + 1}
              </div>
              <div style={{ fontSize: 10, marginTop: 4, opacity: i <= step ? 1 : 0.5 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Step bodies */}
        {step === 0 && (
          <div>
            <h2 style={{ margin: 0, fontFamily: "Fredoka, var(--jop-display)", color: B.gold, fontSize: 22 }}>Add a reference photo</h2>
            <p style={{ fontSize: 13, opacity: 0.75, lineHeight: 1.5, marginTop: 8 }}>
              Clear, well-lit photos work best. Faces, pets, mascots, family — anything the artist can build a pancake from.
            </p>
            <p style={{ fontSize: 12, opacity: 0.55, lineHeight: 1.5, marginTop: 6 }}>
              Supports JPEG, PNG, HEIC (iPhone), WebP, GIF. Max 10&nbsp;MB.
            </p>
            <input ref={fileRef} type="file" accept={PHOTO_ACCEPT_ATTR} onChange={handleFile} style={{ display: "none" }} />
            {photoError && (
              <div style={{ marginTop: 14, padding: "10px 12px", background: "rgba(239,83,126,0.12)", border: `1px solid ${B.pink}`, borderRadius: 8, color: B.pink, fontSize: 13, lineHeight: 1.45 }}>
                {photoError}
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              disabled={photoLoading}
              style={{ width: "100%", marginTop: 18, padding: "14px 16px", background: photoLoading ? "rgba(252,179,31,0.3)" : B.gold, color: B.dark, border: "none", borderRadius: 10, fontWeight: 800, fontSize: 15, cursor: photoLoading ? "wait" : "pointer" }}
            >
              {photoLoading ? "Converting photo…" : (photoError ? "📷 Try a different photo" : "📷 Choose photo")}
            </button>
            <button onClick={onClose} style={secondaryBtn}>Cancel</button>
          </div>
        )}

        {step === 1 && photoFile && (
          <div>
            <h2 style={{ margin: 0, fontFamily: "Fredoka, var(--jop-display)", color: B.gold, fontSize: 20 }}>Position the photo</h2>
            <p style={{ fontSize: 13, opacity: 0.75, lineHeight: 1.5, marginTop: 6, marginBottom: 12 }}>
              Pinch / drag to position. Keep the most important part inside the circle.
            </p>
            <FramingTool imageFile={photoFile} onCropped={setCroppedBlob} onStateChange={setFramingState} />
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button onClick={() => setStep(0)} style={secondaryBtn}>← Back</button>
              <button onClick={() => setStep(2)} disabled={!croppedBlob} style={{ ...primaryBtn, flex: 1, opacity: croppedBlob ? 1 : 0.5 }}>Next →</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 style={{ margin: 0, fontFamily: "Fredoka, var(--jop-display)", color: B.gold, fontSize: 22 }}>Tell us about this one</h2>
            <label style={fieldLabel}>Guest name<span style={{ color: B.pink, marginLeft: 4 }}>*</span></label>
            <input value={name} onChange={e => setName(e.target.value)} maxLength={80} style={fieldInput} placeholder="Who is this pancake for?" />

            <label style={fieldLabel}>Favorite colors <span style={{ opacity: 0.55, fontWeight: 400 }}>(pick up to 3)</span></label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 14 }}>
              {COLOR_OPTIONS.map(color => {
                const selected = colors.includes(color.id);
                const dimmed = !selected && colors.length >= 3;
                return (
                  <button
                    key={color.id}
                    onClick={() => toggleColor(color.id)}
                    disabled={dimmed}
                    style={{
                      aspectRatio: "1", borderRadius: 999, border: selected ? `3px solid ${B.gold}` : "2px solid rgba(255,255,255,0.18)",
                      background: color.hex, cursor: dimmed ? "not-allowed" : "pointer", opacity: dimmed ? 0.4 : 1, padding: 0,
                    }}
                    aria-label={color.label}
                    title={color.label}
                  />
                );
              })}
            </div>

            <label style={fieldLabel}>A note <span style={{ opacity: 0.55, fontWeight: 400 }}>(optional)</span></label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} maxLength={500} style={{ ...fieldInput, resize: "vertical" }} placeholder="Anything we should know — e.g. 'her favorite stuffy is named Bun'" />

            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button onClick={() => setStep(isEdit ? 0 : 1)} style={secondaryBtn}>← Back</button>
              <button onClick={() => setStep(3)} disabled={!name.trim()} style={{ ...primaryBtn, flex: 1, opacity: name.trim() ? 1 : 0.5 }}>Review →</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 style={{ margin: 0, fontFamily: "Fredoka, var(--jop-display)", color: B.gold, fontSize: 22 }}>Looks good?</h2>
            <div style={{ display: "flex", gap: 14, marginTop: 14, alignItems: "flex-start" }}>
              {(croppedUrl || photoPreview) && (
                <img src={croppedUrl || photoPreview} alt="" style={{ width: 110, height: 110, borderRadius: 12, objectFit: "cover", flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#fff" }}>{name || "—"}</div>
                {colors.length > 0 && (
                  <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                    {colors.map(id => {
                      const c = COLOR_OPTIONS.find(o => o.id === id);
                      return c ? <span key={id} style={{ width: 18, height: 18, borderRadius: 999, background: c.hex, border: "1px solid rgba(255,255,255,0.3)" }} /> : null;
                    })}
                  </div>
                )}
                {notes && (
                  <div style={{ fontSize: 13, opacity: 0.78, marginTop: 8, lineHeight: 1.5 }}>{notes}</div>
                )}
              </div>
            </div>
            {submitError && (
              <div style={{ marginTop: 12, color: B.pink, fontSize: 13 }}>{submitError}</div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={() => setStep(2)} disabled={submitting} style={secondaryBtn}>← Back</button>
              <button onClick={handleSubmit} disabled={submitting} style={{ ...primaryBtn, flex: 1, opacity: submitting ? 0.6 : 1 }}>
                {submitting ? "Saving…" : isEdit ? "Save changes" : "Submit"}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────

function CenteredScreen({ text, tone }) {
  return (
    <div style={{ minHeight: "100vh", background: BG, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ maxWidth: 460, textAlign: "center", color: tone === "error" ? B.pink : B.cream, fontSize: 16, lineHeight: 1.55 }}>
        {text}
      </div>
    </div>
  );
}

const fieldLabel = { display: "block", fontSize: 12, fontWeight: 700, color: B.gold, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6, marginTop: 12 };
const fieldInput = {
  width: "100%", padding: "10px 12px", background: "rgba(0,0,0,0.28)", color: "#fff",
  border: "1px solid rgba(255,255,255,0.18)", borderRadius: 8, fontSize: 15, boxSizing: "border-box",
};
const primaryBtn = {
  padding: "12px 16px", background: B.gold, color: B.dark, border: "none", borderRadius: 10,
  fontWeight: 800, fontSize: 15, cursor: "pointer",
};
const secondaryBtn = {
  padding: "12px 16px", background: "transparent", color: B.cream, border: "1px solid rgba(255,218,173,0.3)",
  borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", marginTop: 0,
};
